import store from '@/store'
import { ref, computed } from '@vue/composition-api'
import { mdiEarth, mdiShape, mdiTagOutline } from '@mdi/js'

export default function usePOG() {
    const planogramsData = ref([])

    const validationDialogOpen = ref(false)
    const validationPlanogramId = ref(null)

    const formatLabel = (defaultLabel, selectedArray, type) => {
        if (!selectedArray || selectedArray.length === 0) return defaultLabel;
      
        const names = selectedArray.map(val => getLabelForValue(type, val));
        const tags = names.map(s => `@${s}`).join(' ');
      
        if (tags.length > 25) {
            return `${defaultLabel} (${selectedArray.length} active)`;
        }
        return `${defaultLabel} ${tags}`;
    }
  
    const summaryCards = computed(() => {
        return [
            {
                key: 'markets',
                label: formatLabel('Markets', selectedMarkets.value, 'markets'),
                count: selectedMarkets.value.length > 0 ? selectedMarkets.value.length : facets.value.markets.length,
                icon: mdiEarth,
                color: 'success'
            },
            {
                key: 'project_categories',
                label: formatLabel('Categories', selectedCategory.value ? [selectedCategory.value] : [], 'project_categories'),
                count: selectedCategory.value ? 1 : facets.value.project_categories.length,
                icon: mdiShape,
                color: 'primary'
            },
            {
                key: 'brands',
                label: formatLabel('Brands', selectedBrands.value, 'brands'),
                count: selectedBrands.value.length > 0 ? selectedBrands.value.length : facets.value.brands.length,
                icon: mdiTagOutline,
                color: 'warning'
            },
        ]
    })

    // Data table options
    const options = ref({
        sortBy: ['id'],
        sortDesc: [true],
        orderBy: '',
        page: 1,
        itemsPerPage: 25
    })
    const selectedCategory = ref()
    const selectedMarkets = ref([])
    const selectedBrands = ref([])

    const facets = ref({
        project_categories: [],
        markets: [],
        brands: [],
    })

    const labelCache = ref({})
    const searchQuery = ref('')
    const pogLoading = ref(false)
    const totalPOGs = ref(0)
    const planogram = ref({})
    const downloadLoading = ref(false)
    const imageLoading = ref(false)
    const planogramUploadLoading = ref()
    const categoryLoading = ref(false)
    const categoryOptions = ref([])

    const pogStatus = ref([
        {text: 'Incomplete', value: 'INCO'},
        {text: 'Pending', value: 'PEND'},
        {text: 'In Progress', value: 'PROG'},
        {text: 'Completed', value: 'COMP'},
        {text: 'Cancelled', value: 'CNLD'},
    ])

    const resolveStatus = status => {
        if (status == 'INCO') return { text: 'Incomplete', color: 'error' }
        if (status == 'COMP') return { text: 'Completed', color: 'success' }
        if (status == 'PROG') return { text: 'In Progress', color: 'secondary' }
        if (status == 'PEND') return { text: 'Pending', color: 'warning' }
        if (status == 'CNLD') return { text: 'Cancelled', color: 'error' }
        return { text: 'Incomplete', color: 'error' }
    }
      
    const projectSearchQuery = ref('')
    const projectOptions = ref({
        sortBy: ['job_number'],
        sortDesc: [true],
        orderBy:'',
        page:1,
        itemsPerPage: 10
    })
    const lighthouseProjectsData = ref([])
    const totalProjectListTable = ref(0)
    const projectFields = ['id', 'job_number', 'job_name', 'countries', 'category', 'project_type', 'service_type', 'office', 'client', 'brand', 'business_initiative']
      
    const fetchProjects = async (search='') => {
        store
            .dispatch('app-project/fetchProjects', {
                search: search,
                fields: projectFields,
                options: JSON.stringify(projectOptions.value),
                page: projectOptions.value.page,
            })
            .then(response => {
                const { results, count } = response.data
                lighthouseProjectsData.value = results
                totalProjectListTable.value = count
            })
            .catch(error => {
                console.log(error)
            })
    }

    const downloadMissingProducts = async (instance) => {
        downloadLoading.value = true
        store
            .dispatch('app-skutopia/downloadMissingProducts', instance.id)
            .then(response => {
                const { data } = response
                downloadLoading.value = false
                let fileURL = window.URL.createObjectURL(new Blob([data]));
                let fileLink = document.createElement('a');
            
                fileLink.href = fileURL;
                fileLink.setAttribute('download', `${instance.project}_POG${instance.id}_MissingProducts.xlsx`);
                document.body.appendChild(fileLink);
                fileLink.click();
            })
            .catch(error => {
                downloadLoading.value = false
                console.log(error.response)
                store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
            })
    }

    const downloadDOCXProducts = async (instance) => {
        downloadLoading.value = true
        store
            .dispatch('app-skutopia/downloadDOCXProducts', instance.id)
            .then(response => {
                const { data } = response
                downloadLoading.value = false
                let fileURL = window.URL.createObjectURL(new Blob([data]));
                let fileLink = document.createElement('a');
            
                fileLink.href = fileURL;
                fileLink.setAttribute('download', `${instance.project}_POG${instance.id}_images.docx`);
                document.body.appendChild(fileLink);
                fileLink.click();
            })
            .catch(error => {
                downloadLoading.value = false
                console.log(error.response)
                store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
            })
    }

    const downloadPOGPSD = async (instance) => {
        downloadLoading.value = true
        store
            .dispatch('app-skutopia/downloadPOGPSD', instance.id)
            .then(response => {
                const { data } = response
                downloadLoading.value = false
                let fileURL = window.URL.createObjectURL(new Blob([data]));
                let fileLink = document.createElement('a');
            
                fileLink.href = fileURL;
                fileLink.setAttribute('download', `${instance.project}_POG${instance.id}_PSD.zip`);
                document.body.appendChild(fileLink);
                fileLink.click();
            })
            .catch(error => {
                downloadLoading.value = false
                console.log(error.response)
                store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
            })
    }

    const downloadPNG = async (instance, sides) => {
        downloadLoading.value = true
        store
            .dispatch('app-skutopia/downloadPNG', {id: instance.id, sides: sides})
            .then(response => {
                const { data } = response
                downloadLoading.value = false
                let fileURL = window.URL.createObjectURL(new Blob([data]));
                let fileLink = document.createElement('a');
            
                fileLink.href = fileURL;
                fileLink.setAttribute('download', `${instance.project}_POG${instance.id}_PNG.zip`);
                document.body.appendChild(fileLink);
                fileLink.click();
            })
            .catch(error => {
                downloadLoading.value = false
                console.log(error.response)
                store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
            })
    }

    const downloadDecipherPNG = async (instance, sides) => {
        downloadLoading.value = true
        store
            .dispatch('app-skutopia/downloadDecipherPNG', {id: instance.id, sides: sides})
            .then(response => {
                const { data } = response
                downloadLoading.value = false
                let fileURL = window.URL.createObjectURL(new Blob([data]));
                let fileLink = document.createElement('a');
            
                fileLink.href = fileURL;
                fileLink.setAttribute('download', `${instance.project}_POG${instance.id}_Decipher.zip`);
                document.body.appendChild(fileLink);
                fileLink.click();
            })
            .catch(error => {
                downloadLoading.value = false
                console.log(error.response)
                store.commit('comp-alert/SET_MESSAGE', error.response.statusText)
            })
    }

    const downloadPOGXLSX = async (pog, includeMissing = true) => {
        downloadLoading.value = true
        return store.dispatch('app-skutopia/downloadPOGXLSX', {
            id: pog.id,
            includeMissing
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${pog.project}_POG${pog.id}_POG_File.xlsx`);
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            downloadLoading.value = false
        })
        .catch(error => {
            downloadLoading.value = false
            console.log(error)
        })
    }

    const addPOGShelfImage = async (id, file) => {
        const payload = {
            id: id,
            file: file
        }
        imageLoading.value = true
        return await store
            .dispatch('app-skutopia/addPOGShelfImage', payload)
            .then(response => {
                imageLoading.value = false
                return response.data
            })
            .catch(error => {
                imageLoading.value = false
                console.log(error)
            })
    }

    const fetchPOGs = async (params = {}) => {
        pogLoading.value = true
        const paramsQuery = {
            search: searchQuery.value || "",
            category: selectedCategory.value,                          
            market: selectedMarkets.value.join(','),                    
            brand: selectedBrands.value.join(','),                      
            options: JSON.stringify(options.value),
            page: options.value.page,
        }
        
        Object.keys(params).forEach(key => {
            paramsQuery[key] = params[key]
        })
    
        store
            .dispatch('app-skutopia/fetchPOGs', paramsQuery)
            .then(response => {
                const { results, count } = response.data
                planogramsData.value = results
                totalPOGs.value = count
                fetchPOGFacets(paramsQuery)   
                pogLoading.value = false
            })
            .catch(error => {
                console.log(error)
                pogLoading.value = false
            })
    }

    const fetchPOG = async (id) => {
        pogLoading.value = true
        await store
            .dispatch('app-skutopia/fetchPOG', id, {})
            .then(response => {
                pogLoading.value = false
                planogram.value =  response.data
            })
            .catch(error => {
                pogLoading.value = false
                console.log(error)
            })
    }

    const deletePOG = async (id) => {
        pogLoading.value = true
        await store
            .dispatch('app-skutopia/deletePOG', id)
            .then(response => {
                pogLoading.value = false
                fetchPOGs()
            })
            .catch(error => {
                pogLoading.value = false
                console.log(error)
            })
    }

    const addPOG = async (project, file) => {
    const formPOG = new FormData()
    formPOG.append('file', file)
    formPOG.append(`job_number`, project.job_number);
    formPOG.append(`job_name`, project.job_name);
    formPOG.append(`country`, project.country_code);
    formPOG.append(`category`,  project.category);
    
    planogramUploadLoading.value = true

    await store
        .dispatch('app-skutopia/addPOG', formPOG)
        .then(response => {
            const { data } = response
            planogramUploadLoading.value = false
        
            // Check for explicit planogram_id OR the nested planogram.id
            const pogId = data.planogram_id || (data.planogram && data.planogram.id)
            
            if (pogId) {
                validationPlanogramId.value = pogId
                validationDialogOpen.value = true
            }
        })
        .catch(error => {
            planogramUploadLoading.value = false
            console.log(error)
        })
}

    const editPOG = async (id, payload) => {
        const formPOG = new FormData()
        for (const [key, value] of Object.entries(payload)) {
            formPOG.append(key, value)
        }
        planogramUploadLoading.value = true

        return await store
            .dispatch('app-skutopia/editPOG', {id, payload:formPOG})
            .then(response => {
                planogramUploadLoading.value = false
                return response.data
            })
            .catch(error => {
                planogramUploadLoading.value = false
                console.log(error)
            })
    }
        
    const fetchCategories = async () => {
        categoryLoading.value = true
        await store
            .dispatch('app-skutopia/fetchProjCategories', {})
            .then(response => {
                categoryOptions.value = response.data
                categoryLoading.value = false
            })
            .catch(error => {
                console.log(error)
                categoryLoading.value = false
            })
    }

    const updatePOGProduct = async (payload) => {
        pogLoading.value = true
        await store
            .dispatch('app-skutopia/updatePOGProduct', payload)
            .then(response => {
                pogLoading.value = false
            })
            .catch(error => {
                pogLoading.value = false
                console.log(error)
            })
    }

    const getTargetRef = (type) => {
        switch (type) {
            case 'markets': return selectedMarkets
            case 'brands': return selectedBrands
            default: return null
        }
    }
  
    const getLabelForValue = (type, value) => {
        const list = facets.value[type] || []
        const found = list.find(item => item.value == value)
      
        if (found) {
            labelCache.value[`${type}_${value}`] = found.label
            return found.label
        }
      
        if (labelCache.value[`${type}_${value}`]) {
            return labelCache.value[`${type}_${value}`]
        }
      
        return value
    }
    
    const getFacetOptions = (type) => {
        const apiList = facets.value[type] || []
        const selectedIds = getTargetRef(type).value || []
      
        const missingSelected = selectedIds.filter(id => !apiList.find(i => i.value == id))
        const restoredItems = missingSelected.map(id => ({
            value: id,
            label: getLabelForValue(type, id),
            count: 0
        }))
      
        return [...restoredItems, ...apiList]
    }
    
    const fetchPOGFacets = async (paramsQuery) => {
        return store
            .dispatch('app-skutopia/fetchPOGFacets', paramsQuery)
            .then(response => {
                facets.value = response.data
            })
            .catch(err => console.error("Error fetching POG facets", err))
    }

    return {
        planogramsData,
        options,
        searchQuery,
        pogLoading,
        totalPOGs,
        pogStatus,
        planogram,
        downloadPOGPSD,
        downloadPNG,
        downloadDecipherPNG,
        downloadPOGXLSX,
        imageLoading,
        downloadLoading,
        downloadMissingProducts,
        downloadDOCXProducts,
        planogramUploadLoading,
        projectSearchQuery,
        projectOptions,
        lighthouseProjectsData,
        totalProjectListTable,
        categoryLoading,
        categoryOptions,

        selectedBrands,
        selectedMarkets,
        facets,
        labelCache,
        getTargetRef,
        getFacetOptions,
        getLabelForValue,
        fetchPOGFacets,
    
        fetchProjects,
        fetchCategories,

        addPOGShelfImage,
        resolveStatus,
        fetchPOGs,
        fetchPOG,
        addPOG,
        editPOG,
        deletePOG,
        updatePOGProduct,
        validationDialogOpen,
        validationPlanogramId,

        summaryCards,
        formatLabel
    }
}